<script>
  import { createEventDispatcher } from "svelte";
  import {
    DataTable,
    Toolbar,
    ToolbarContent,
    ToolbarSearch,
  } from "carbon-components-svelte";
  import EditIcon from "../../routes/app/settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../routes/app/settings/svg/RemoveIcon.svelte";
  import PlusIcon from "@components/common/icons/app/PlusIcon.svelte";
  import { Tooltip, Button, Pagination } from "paxray-components-svelte";
  import { PLUS_ICON } from '@utils/icons.js';

  const dispatch = createEventDispatcher();

  export let headers = [];
  export let rows = [];
  export let totalItems = 0;
  export let searchValue = "";
  export let sortable = true;
  export let size = undefined;
  export let expandable = false;
  export let batchExpansion = false;
  export let batchSelection = false;
  export let selectedRowIds = [];
  export let expandedRowIds = [];
  export let nonExpandableRowIds = [];

  export let sortKey = null;
  export let sortDirection = "none";

  export let page = 1;
  export let pageSize = 10;
  export let pageSizes = [10, 25, 50, 75, 100];

  // Let the underlying DataTable slice the rows by page/pageSize.
  // Keep false when the parent already passes a pre-sliced page of rows.
  export let slicePage = false;

  export let showToolbar = true;
  export let showSearch = true;
  export let showPagination = true;
  export let showNewButton = true;
  export let newButtonText = "New";
  export let newButtonIcon = PLUS_ICON;;
  export let newButtonIconLeft = true;
  export let autoCountColumnStyle = false;

  // Column keys whose array value should render as a comma-joined list of names.
  export let arrayKeys = [
    "projects",
    "departments",
    "objects",
    "processes",
    "subprocesses",
    "tasks",
  ];

  // columnActions: maps column key → action button configs.
  // Each action: { icon, label, danger?, disabled?: boolean | ((row) => boolean), onClick: (row) => void }
  // columnActions takes priority over the custom cell slot and the default rendering.
  export let columnActions = {};
  // Shown below the header when there are no rows, so an empty table explains itself. Empty string keeps the old look.
  export let emptyText = "";
</script>

<DataTable
  class={`custom-table ${$$slots["left-toolbar"] ? "custom-table-toolbar" : ""} ${autoCountColumnStyle ? "count-column-style" : ""}`}
  {headers}
  {rows}
  {sortable}
  {size}
  {expandable}
  {batchExpansion}
  {batchSelection}
  {nonExpandableRowIds}
  bind:sortKey
  bind:sortDirection
  bind:selectedRowIds
  bind:expandedRowIds
  on:click:header--select
  page={slicePage ? page : undefined}
  pageSize={slicePage ? pageSize : undefined}
>
  {#if showToolbar}
    <Toolbar>
      <ToolbarContent>
        <slot name="left-toolbar" class="left-toolbar" />
        {#if showSearch}
          <ToolbarSearch
            persistent
            value={searchValue}
            on:input={(e) => dispatch("search", { value: e.target.value })}
            on:clear={() => dispatch("clearSearch")}
          />
        {/if}
        {#if showNewButton}
          <div class="custom-table-toolbar-button">
            <Button
              kind="secondary"
              iconLeft={newButtonIconLeft}
              text={newButtonText}
              icon={newButtonIcon}
              on:click={() => dispatch("addNew")}
            />
          </div>
        {/if}
        <slot name="right-toolbar" />
      </ToolbarContent>
    </Toolbar>
  {/if}
  <svelte:fragment slot="cell-header" let:header>
    <slot name="cell-header" {header}>
      {#if header.tooltip}
        <div class="header_with_tooltip">
          {header.value}
          <Tooltip>{header.tooltip}</Tooltip>
        </div>
      {:else}
        {header.value}
      {/if}
    </slot>
  </svelte:fragment>
  <svelte:fragment slot="cell" let:row let:cell>
    {#if columnActions[cell.key]}
      <div class="cell-center">
        {#each columnActions[cell.key] as action}
          <button
            class="edit-remove-btn"
            class:danger-btn={action.danger}
            disabled={typeof action.disabled === "function"
              ? action.disabled(row)
              : !!action.disabled}
            on:click={() => action.onClick(row)}
            aria-label={action.label}
            title={action.label}
          >
            <svelte:component this={action.icon} />
          </button>
        {/each}
      </div>
    {:else}
      <slot name="cell" {row} {cell}>
        {#if cell.key === "edit"}
          <div class="cell-center">
            <button
              class="edit-remove-btn"
              on:click={() => dispatch("edit", { row })}><EditIcon /></button
            >
          </div>
        {:else if cell.key === "actions"}
          <div class="cell-center">
            <button
              class="edit-remove-btn"
              on:click={() => dispatch("action", { row })}><PlusIcon /></button
            >
          </div>
        {:else if cell.key === "delete"}
          <div class="cell-center">
            <button
              class="edit-remove-btn"
              on:click={() => dispatch("delete", { row })}
              ><RemoveIcon /></button
            >
          </div>
        {:else if arrayKeys.includes(cell.key) && Array.isArray(cell.value)}
          {cell.value.map((item) => item.name).join(", ")}
        {:else}
          {cell?.value || "-"}
        {/if}
      </slot>
    {/if}
  </svelte:fragment>
  <svelte:fragment slot="expanded-row" let:row>
    <slot name="expanded-row" {row} />
  </svelte:fragment>
</DataTable>
{#if emptyText && !rows.length}
  <div class="custom-table-empty">{emptyText}</div>
{/if}
{#if showPagination}
  <Pagination bind:pageSize bind:page {totalItems} {pageSizes} dark />
{/if}

<style>
  .custom-table-empty {
    padding: 1.5rem 1rem;
    border-bottom: 1px solid var(--border-color);
    background: var(--pax-white);
    color: var(--pax-grey);
    font-size: 0.875rem;
    text-align: center;
  }

  .cell-center {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
  }

  .edit-remove-btn {
    background: var(--pax-black);
    width: 30px;
    height: 30px;
    border-radius: 4px;
    display: grid;
    place-items: center;
    cursor: pointer;
    color: white;
  }

  .edit-remove-btn:disabled {
    opacity: 0.38;
    cursor: not-allowed;
  }

  .edit-remove-btn.danger-btn:not(:disabled):hover {
    background: #da1e28;
  }

  .header_with_tooltip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  :global(.custom-table-toolbar-button .button-secondary) {
    border: 1px solid var(--pax-smoke);
  }

  :global(.options-container .filter-wrapper .bx--form-item) {
    max-width: 12.5rem;
  }

  :global(.custom-table .bx--toolbar-content) {
    margin-bottom: 0.5rem;
    gap: 0.5rem;
  }

  :global(.custom-table .bx--toolbar-content .bx--search-input) {
    height: 3rem;
    min-height: 3rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background-color: var(--pax-white) !important;
    color: var(--pax-black);
  }

  :global(
      .custom-table
        .bx--toolbar-content
        .bx--search-input:focus:not([disabled]),
      .custom-table
        .bx--toolbar-search-container-persistent
        .bx--search-input:focus:not([disabled])
    ) {
    outline: 1px solid var(--pax-black);
    outline-offset: 0;
  }

  :global(
      .custom-table .bx--toolbar-content .bx--search-close:focus,
      .custom-table .bx--toolbar-content .bx--search-button:focus,
      .custom-table .bx--toolbar-content .bx--search-close:active,
      .custom-table .bx--toolbar-content .bx--search-button:active,
      .custom-table .bx--toolbar-content .bx--search-close:hover,
      .custom-table .bx--toolbar-content .bx--search-button:hover,
      .custom-table .bx--search-input:focus ~ .bx--search-close:hover
    ) {
    outline: 1px solid var(--pax-black);
    outline-offset: -1px;
    border-radius: 0.5rem;
  }

  :global(
      .custom-table.count-column-style th[data-header="count"] .bx--table-sort
    ) {
    padding-left: 0;
  }

  :global(
      .custom-table.count-column-style
        th[data-header="count"]
        .bx--table-sort
        svg
    ) {
    padding-left: 0;
    margin-left: 0;
  }

  :global(
      .custom-table thead th.bx--table-column-checkbox
        .bx--checkbox:not(:checked):not(:indeterminate)
        + .bx--checkbox-label::before
    ) {
    border-color: #ffffff;
    background-color: #ffffff;
  }

</style>
